import { useEffect, useMemo, useState } from 'react';
import { StudyData } from '../cornerstone/init';
import { LayoutType, useViewerStore } from '../stores/viewerStore';
import {
  HangingProtocol,
  ViewportAssignment,
  listProtocols,
  saveProtocol,
  deleteProtocol,
  captureCurrentView,
} from '../utils/hangingProtocols';
import { ViewportGridHandle } from './ViewportGrid';
import { presetsForModality } from '../utils/wlPresets';
import styles from './Dialog.module.css';
import protoStyles from './ProtocolsDialog.module.css';

const LAYOUTS: LayoutType[] = [
  '1x1', '1x2', '1x3', '2x1', '2x2', '2x3', '3x1', '3x2', '3x3',
];

interface Props {
  studyData: StudyData;
  onClose: () => void;
  /** Called when the user clicks Apply on a specific protocol. Parent
   *  is responsible for setting the layout + populating each viewport
   *  per the protocol's config (typically via applyHangingProtocol). */
  onApply: (protocol: HangingProtocol) => void;
}

/** Viewport count implied by a "RxC" layout string. */
function viewportCount(layout: LayoutType): number {
  const [r, c] = layout.split('x').map((n) => parseInt(n, 10) || 1);
  return r * c;
}

/** Fill the config array up to `count`, carrying over user-entered rules. */
function normaliseConfig(cfg: ViewportAssignment[] | undefined, count: number): ViewportAssignment[] {
  const out: ViewportAssignment[] = [];
  for (let i = 0; i < count; i++) {
    const existing = (cfg || []).find((c) => c.vp === i);
    out.push(existing ?? { vp: i, seriesMatch: { index: i } });
  }
  return out;
}

export default function ProtocolsDialog({ studyData, onClose, onApply }: Props) {
  const [protocols, setProtocols] = useState<HangingProtocol[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<HangingProtocol> | null>(null);

  async function refresh() {
    setLoading(true);
    setProtocols(await listProtocols());
    setLoading(false);
  }

  useEffect(() => { refresh(); }, []);

  async function remove(id: number) {
    if (!confirm('Delete this protocol?')) return;
    if (await deleteProtocol(id)) refresh();
  }

  async function save() {
    if (!editing) return;
    const ok = await saveProtocol(editing);
    if (ok != null) {
      setEditing(null);
      refresh();
    }
  }

  function startNew() {
    setEditing({
      name: 'New Protocol',
      modality: studyData.modality,
      priority: 100,
      layout: '1x2',
      config: normaliseConfig(undefined, 2),
      isDefault: false,
    });
  }

  /** Snapshot the current viewer state (layout + what's in each viewport +
   *  WL / invert / current slice) and drop the user into the editor with it
   *  pre-populated. They still pick a name + press Save. */
  function captureCurrent() {
    const layout = useViewerStore.getState().layout;
    const config = captureCurrentView(studyData);
    if (config.length === 0) {
      alert('No series are loaded in any viewport yet — load at least one series first.');
      return;
    }
    setEditing({
      name: `${studyData.modality || ''} ${layout}`.trim() || 'Captured view',
      modality: studyData.modality,
      priority: 100,
      layout,
      config: normaliseConfig(config, layout.split('x').reduce((a, b) => a * parseInt(b, 10), 1)),
      isDefault: false,
    });
  }

  function updateEditing(patch: Partial<HangingProtocol>) {
    if (!editing) return;
    const next = { ...editing, ...patch };
    // Layout change → grow/shrink the config array
    if (patch.layout && patch.layout !== editing.layout) {
      next.config = normaliseConfig(editing.config, viewportCount(patch.layout));
    }
    setEditing(next);
  }

  function updateAssignment(vp: number, patch: Partial<ViewportAssignment>) {
    if (!editing?.config) return;
    setEditing({
      ...editing,
      config: editing.config.map((a) => (a.vp === vp ? { ...a, ...patch } : a)),
    });
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={`${styles.dialog} ${styles.dialogLarge}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>Hanging Protocols</h3>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.body}>
          {!editing && (
            <>
              {loading && <div>Loading…</div>}
              {!loading && protocols.length === 0 && (
                <div className={styles.empty}>No protocols defined yet.</div>
              )}
              {protocols.map((p) => (
                <div key={p.id} className={styles.listItem} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div className={styles.itemTitle}>{p.name}</div>
                    <div className={styles.itemSub}>
                      {p.modality || 'any'} · {p.layout} · priority {p.priority}
                      {p.isDefault ? ' · default' : ''}
                    </div>
                  </div>
                  <button
                    className={styles.secondaryBtn}
                    onClick={() => { onApply(p); onClose(); }}
                  >Apply</button>
                  <button className={styles.secondaryBtn} onClick={() => setEditing(p)}>Edit</button>
                  <button className={styles.dangerBtn} onClick={() => remove(p.id)}>Delete</button>
                </div>
              ))}
            </>
          )}

          {editing && (
            <ProtocolEditor
              editing={editing}
              studyData={studyData}
              onPatchProtocol={updateEditing}
              onPatchAssignment={updateAssignment}
            />
          )}
        </div>

        <div className={styles.footer}>
          {editing ? (
            <>
              <button className={styles.secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
              <button className={styles.primaryBtn} onClick={save}>Save</button>
            </>
          ) : (
            <>
              <button className={styles.secondaryBtn} onClick={onClose}>Close</button>
              <button className={styles.secondaryBtn} onClick={startNew}>Blank Protocol</button>
              <button className={styles.primaryBtn} onClick={captureCurrent}>
                Save Current View
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================

interface EditorProps {
  editing: Partial<HangingProtocol>;
  studyData: StudyData;
  onPatchProtocol: (patch: Partial<HangingProtocol>) => void;
  onPatchAssignment: (vp: number, patch: Partial<ViewportAssignment>) => void;
}

function ProtocolEditor({ editing, studyData, onPatchProtocol, onPatchAssignment }: EditorProps) {
  const presets = useMemo(
    () => presetsForModality(editing.modality),
    [editing.modality]
  );

  const availableSeries = studyData.series.map((s, idx) => ({
    idx,
    seriesId: s.seriesId,
    seriesNumber: s.seriesNumber,
    modality: s.modality,
    desc: s.seriesDescription || `Series ${s.seriesNumber}`,
    numImages: s.numImages,
  }));

  return (
    <>
      <div className={protoStyles.protocolMeta}>
        <div>
          <label className={styles.label}>Name</label>
          <input
            className={styles.input}
            value={editing.name || ''}
            onChange={(e) => onPatchProtocol({ name: e.target.value })}
          />
        </div>
        <div>
          <label className={styles.label}>Modality (blank = any)</label>
          <input
            className={styles.input}
            value={editing.modality || ''}
            onChange={(e) => onPatchProtocol({ modality: e.target.value.toUpperCase() })}
          />
        </div>
        <div>
          <label className={styles.label}>Body part</label>
          <input
            className={styles.input}
            value={editing.bodyPart || ''}
            onChange={(e) => onPatchProtocol({ bodyPart: e.target.value })}
          />
        </div>
        <div>
          <label className={styles.label}>Layout</label>
          <select
            className={styles.select}
            value={editing.layout || '1x1'}
            onChange={(e) => onPatchProtocol({ layout: e.target.value as LayoutType })}
          >
            {LAYOUTS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className={styles.label}>Priority (higher wins)</label>
          <input
            type="number"
            className={styles.input}
            value={editing.priority ?? 100}
            onChange={(e) => onPatchProtocol({ priority: Number(e.target.value) })}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'end', paddingBottom: 10 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={!!editing.isDefault}
              onChange={(e) => onPatchProtocol({ isDefault: e.target.checked })}
            />
            <span>Default for this modality</span>
          </label>
        </div>
      </div>

      {/* Reference — available series in the current study */}
      <details className={protoStyles.seriesPanel}>
        <summary>Series in this study ({availableSeries.length}) — reference for matching</summary>
        <table className={protoStyles.refTable}>
          <thead>
            <tr><th>Idx</th><th>SN</th><th>Mod</th><th>Description</th><th>Images</th></tr>
          </thead>
          <tbody>
            {availableSeries.map((s) => (
              <tr key={s.seriesId}>
                <td>{s.idx}</td>
                <td>{s.seriesNumber}</td>
                <td>{s.modality}</td>
                <td>{s.desc}</td>
                <td>{s.numImages}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>

      <h4 className={protoStyles.sectionTitle}>Viewport assignments</h4>
      <div className={protoStyles.viewportList}>
        {(editing.config || []).map((asgn) => (
          <ViewportRow
            key={asgn.vp}
            assignment={asgn}
            presetOptions={presets}
            onPatch={(patch) => onPatchAssignment(asgn.vp, patch)}
          />
        ))}
      </div>
    </>
  );
}

// =============================================================================

interface RowProps {
  assignment: ViewportAssignment;
  presetOptions: { label: string }[];
  onPatch: (patch: Partial<ViewportAssignment>) => void;
}

function ViewportRow({ assignment, presetOptions, onPatch }: RowProps) {
  const [showDisplay, setShowDisplay] = useState(
    !!assignment.display && Object.keys(assignment.display).length > 0
  );

  const match = assignment.seriesMatch;
  const display = assignment.display || {};

  function patchMatch(patch: Partial<ViewportAssignment['seriesMatch']>) {
    onPatch({ seriesMatch: { ...match, ...patch } });
  }

  function patchDisplay(patch: Partial<NonNullable<ViewportAssignment['display']>>) {
    onPatch({ display: { ...display, ...patch } });
  }

  return (
    <div className={protoStyles.vpRow}>
      <div className={protoStyles.vpBadge}>VP {assignment.vp}</div>

      <div className={protoStyles.matchFields}>
        <div className={protoStyles.fieldGroup}>
          <label>Modality</label>
          <input
            type="text"
            placeholder="any"
            value={match.modality ?? ''}
            onChange={(e) => patchMatch({ modality: e.target.value ? e.target.value.toUpperCase() : undefined })}
          />
        </div>
        <div className={protoStyles.fieldGroup}>
          <label>Description contains</label>
          <input
            type="text"
            placeholder="e.g. axial"
            value={match.desc ?? ''}
            onChange={(e) => patchMatch({ desc: e.target.value || undefined })}
          />
        </div>
        <div className={protoStyles.fieldGroup}>
          <label>Series #</label>
          <input
            type="text"
            placeholder="exact"
            value={match.seriesNumber ?? ''}
            onChange={(e) => patchMatch({ seriesNumber: e.target.value || undefined })}
          />
        </div>
        <div className={protoStyles.fieldGroup}>
          <label>Index</label>
          <input
            type="number"
            min={0}
            placeholder="order"
            value={match.index ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              patchMatch({ index: v === '' ? undefined : Number(v) });
            }}
          />
        </div>
      </div>

      <button
        type="button"
        className={`${protoStyles.displayToggle} ${showDisplay ? protoStyles.displayToggleActive : ''}`}
        onClick={() => setShowDisplay(!showDisplay)}
        title="Display settings"
      >
        {showDisplay ? '▾ Display' : '▸ Display'}
      </button>

      {showDisplay && (
        <div className={protoStyles.displayFields}>
          <div className={protoStyles.fieldGroup}>
            <label>W/L preset</label>
            <select
              value={display.wlPreset ?? ''}
              onChange={(e) => patchDisplay({ wlPreset: e.target.value || undefined })}
            >
              <option value="">—</option>
              {presetOptions.map((p) => (
                <option key={p.label} value={p.label}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className={protoStyles.fieldGroup}>
            <label>or WW</label>
            <input
              type="number"
              placeholder="width"
              value={display.wlWindow ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                patchDisplay({ wlWindow: v === '' ? undefined : Number(v) });
              }}
            />
          </div>
          <div className={protoStyles.fieldGroup}>
            <label>WC</label>
            <input
              type="number"
              placeholder="center"
              value={display.wlCenter ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                patchDisplay({ wlCenter: v === '' ? undefined : Number(v) });
              }}
            />
          </div>

          <div className={protoStyles.fieldGroup}>
            <label>Initial slice</label>
            <select
              value={typeof display.initialSlice === 'string' ? display.initialSlice : ''}
              onChange={(e) => patchDisplay({ initialSlice: (e.target.value as any) || undefined })}
            >
              <option value="">—</option>
              <option value="first">First</option>
              <option value="middle">Middle</option>
              <option value="last">Last</option>
            </select>
          </div>

          <div className={protoStyles.fieldGroup}>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={!!display.invert}
                onChange={(e) => patchDisplay({ invert: e.target.checked || undefined })}
              />
              Invert
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
